import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines multiple class name strings using clsx and tailwind-merge
 */
export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs));
}

/**
 * Type guard to check if a value is not null or undefined
 */
export function notNothing<T>(maybeNull: T | null | undefined): maybeNull is T {
  return maybeNull != null;
}

/**
 * Converts a Maybe type (which could be undefined) to a nullable type (explicitly null or T)
 * @param value - A value that might be undefined or null
 * @returns The original value if it exists, null otherwise
 */
export const nullable = <T>(value: Maybe<T>) => {
  if (!value) return null;
  return value;
};

/**
 * Type for converting an object's entries to a tuple array while preserving exact types
 */
export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

/**
 * Gets the entries of an object with proper TypeScript types, preserving the exact
 * key-value relationship.
 *
 * @example
 * const obj = { a: 1, b: "two" } as const;
 * const entries = entriesFromObject(obj);
 * // entries: [["a", 1] | ["b", "two"]][]
 */
export function entriesFromObject<T extends object>(object: T): Entries<T> {
  return Object.entries(object) as Entries<T>;
}
